import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Container, Box, Button, IconButton, Tooltip, useScrollTrigger } from '@mui/material';
import { Link } from 'react-router-dom';
import { Login as LoginIcon } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import SignInDialog from './SignInDialog';
import UserMenu from './UserMenu';

const AppLayout = ({ children }) => {
  const { isArabic, toggleLanguage, t } = useLanguage();
  const { user, loading } = useAuth();
  const [signInOpen, setSignInOpen] = useState(false);
  
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  const handleSignInOpen = () => {
    setSignInOpen(true);
  };

  const handleSignInClose = () => {
    setSignInOpen(false);
  };

  const navButtonSx = {
    color: 'text.primary',
    fontWeight: 600,
    fontSize: { xs: '0.875rem', sm: '0.95rem' },
    px: 2,
    py: 1,
    borderRadius: 2,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      color: 'primary.main',
    },
  };

  const authButtonSx = {
    ml: { xs: 1, sm: 2 },
    borderRadius: 2,
    px: 2,
    py: 1,
    textTransform: 'none',
    fontSize: { xs: '0.875rem', sm: '0.95rem' },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', direction: isArabic ? 'rtl' : 'ltr' }}>
      <AppBar 
        position="sticky" 
        color="default"
        elevation={trigger ? 4 : 0}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: trigger ? 'none' : '1px solid',
          borderColor: 'divider',
          transition: 'all 0.2s',
        }}
      >
        <Toolbar sx={{
          minHeight: { xs: '64px', sm: '72px' },
          padding: { xs: '0 16px', sm: '0 24px' },
          justifyContent: 'space-between',
        }}>
          {isArabic ? (
            // Arabic layout
            <>
              {/* Navigation & Auth - Left side */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button 
                  component={Link} 
                  to="/"
                  sx={navButtonSx}
                >
                  {t('Home', 'الرئيسية')}
                </Button>
                <Button 
                  component={Link} 
                  to="/about"
                  sx={navButtonSx}
                >
                  {t('About', 'من نحن')}
                </Button>
                
                {/* Auth Section */}
                {!loading && (
                  user ? (
                    <Box sx={{ ml: 1 }}>
                      <UserMenu />
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<LoginIcon />}
                      onClick={handleSignInOpen}
                      sx={authButtonSx}
                    >
                      {t('Sign In', 'تسجيل الدخول')}
                    </Button>
                  )
                )}
                
                <Tooltip title={t('Switch to English', 'التبديل إلى الإنجليزية')}>
                  <IconButton 
                    onClick={toggleLanguage}
                    size="small"
                    sx={{ 
                      ml: 1,
                      width: 40,
                      height: 40,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ fontWeight: 700, color: 'text.secondary' }}
                    >
                      {isArabic ? 'EN' : 'ع'}
                    </Typography>
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Logo - Right side */}
              <Box 
                component={Link} 
                to="/" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  textDecoration: 'none',
                  ml: 'auto'
                }}
              >
                <Box
                  component="img"
                  src={isArabic ? "/images/logo.png" : "/images/english logo.png"}
                  alt={t("Me'ar Logo", "شعار معيار")}
                  sx={{
                    height: { xs: '32px', sm: '48px' },
                    width: 'auto',
                    // Removed filter to show original logo colors
                  }}
                />
              </Box>
            </>
          ) : (
            // English layout
            <>
              {/* Logo - Left side */}
              <Box 
                component={Link} 
                to="/" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  textDecoration: 'none',
                  mr: 'auto'
                }}
              >
                <Box
                  component="img"
                  src={isArabic ? "/images/logo.png" : "/images/english logo.png"}
                  alt={t("Me'ar Logo", "شعار معيار")}
                  sx={{
                    height: { xs: '32px', sm: '48px' },
                    width: 'auto',
                    // Removed filter
                  }}
                />
              </Box>
              
              {/* Navigation & Auth - Right side */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button 
                  component={Link} 
                  to="/"
                  sx={navButtonSx}
                >
                  {t('Home', 'الرئيسية')}
                </Button>
                <Button 
                  component={Link} 
                  to="/about"
                  sx={navButtonSx}
                >
                  {t('About', 'حول')}
                </Button>
                
                {!loading && (
                  user ? (
                    <Box sx={{ ml: 1 }}>
                      <UserMenu />
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<LoginIcon />}
                      onClick={handleSignInOpen}
                      sx={authButtonSx}
                    >
                      {t('Sign In', 'تسجيل الدخول')}
                    </Button>
                  )
                )}
                
                <Tooltip title={t('Switch to Arabic', 'التبديل إلى العربية')}>
                  <IconButton 
                    onClick={toggleLanguage}
                    size="small"
                    sx={{ 
                      ml: 1,
                      width: 40,
                      height: 40,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ fontWeight: 700, color: 'text.secondary' }}
                    >
                      {isArabic ? 'EN' : 'ع'}
                    </Typography>
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ mt: 0, mb: 0, flexGrow: 1, minHeight: 'calc(100vh - 64px)' }}>
        {children}
      </Box>

      <Box component="footer" sx={{ bgcolor: 'grey.900', color: 'grey.400', py: 4, mt: 'auto' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              {t('© 2025 Me\'ar Technology. All rights reserved.', '© 2025 تقنية معيار. جميع الحقوق محفوظة.')}
            </Typography>
            <Link to="/about" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.875rem' }}>
              {t('About Us', 'من نحن')}
            </Link>
          </Box>
        </Container>
      </Box>
      
      <SignInDialog open={signInOpen} onClose={handleSignInClose} />
    </Box>
  );
};

export default AppLayout;